import React, { useState } from 'react';
import { Layers, Eye, ArrowRight, Tag } from 'lucide-react';
import { portfolioData } from '../data/portfolioData';
import { ProjectItem } from '../types';

interface PortfolioPageProps {
  onSelectProject: (project: ProjectItem) => void;
  onOpenConsultation: () => void;
}

export const PortfolioPage: React.FC<PortfolioPageProps> = ({ onSelectProject, onOpenConsultation }) => {
  const [activeCategory, setActiveCategory] = useState<string>('All');
  const categories = ['All', 'Web Platforms', 'Mobile Experiences', 'Business Software', 'AI & Automation', 'E-Commerce', 'Digital Marketing'];

  const filtered = activeCategory === 'All'
    ? portfolioData
    : portfolioData.filter(p => p.category === activeCategory);

  return (
    <div className="bg-[#071426] text-[#D5DFEC] py-12 space-y-16">
      
      {/* Header */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl space-y-4">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#0B2342] border border-[#1b2f4a] text-xs font-semibold uppercase tracking-wider text-[#FF851B]">
            <span>FEATURED WORK & CASE STUDIES</span>
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white font-display tracking-tight leading-tight">
            Ideas Transformed Into Digital Experiences
          </h1>
          <p className="text-base sm:text-lg text-[#D5DFEC] leading-relaxed">
            A curated showcase of representative enterprise architectures, cloud platforms, and intuitive cross-platform applications.
          </p>
        </div>
      </section>

      {/* Categories & Grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
        
        {/* Filter Tabs */}
        <div className="flex flex-wrap items-center gap-2">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-4 py-2 rounded-full text-xs font-semibold transition-all ${
                activeCategory === cat
                  ? 'bg-[#159DE8] text-white shadow-glow-blue'
                  : 'bg-[#0c1a2d] border border-[#1b2f4a] text-[#8FA0B8] hover:text-white hover:bg-[#10223b]'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filtered.map((project) => (
            <div
              key={project.id}
              className="rounded-2xl bg-[#0c1a2d] border border-[#1b2f4a] overflow-hidden hover:border-[#159DE8]/50 transition-all duration-300 shadow-card-dark flex flex-col justify-between group"
            >
              <div>
                <div className="relative aspect-[16/10] overflow-hidden bg-[#050A13]">
                  <img
                    src={project.image}
                    alt={project.title}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0c1a2d] via-[#0c1a2d]/20 to-transparent opacity-75" />
                  
                  <div className="absolute top-3 left-3">
                    <span className="px-2.5 py-1 rounded-md bg-[#050A13]/90 backdrop-blur-md border border-[#1b2f4a] text-[11px] font-semibold text-[#159DE8]">
                      {project.category}
                    </span>
                  </div>

                  <button
                    onClick={() => onSelectProject(project)}
                    className="absolute inset-0 flex items-center justify-center bg-[#050A13]/60 opacity-0 group-hover:opacity-100 transition-opacity"
                    aria-label={`View details for ${project.title}`}
                  >
                    <span className="px-4 py-2 rounded-full bg-white text-[#050A13] text-xs font-bold shadow-lg flex items-center gap-1.5 hover:scale-105 transition-transform">
                      <Eye size={14} />
                      <span>View Case Study</span>
                    </span>
                  </button>
                </div>

                <div className="p-6 space-y-3">
                  <h2 className="text-lg font-bold text-white group-hover:text-[#FF851B] transition-colors font-display">
                    {project.title}
                  </h2>
                  <p className="text-xs sm:text-sm text-[#8FA0B8] leading-relaxed">
                    {project.summary}
                  </p>
                </div>
              </div>

              <div className="p-6 pt-0 space-y-4">
                <div className="flex flex-wrap gap-1.5">
                  {project.tags.map((tag, i) => (
                    <span key={i} className="text-[10px] px-2 py-0.5 rounded bg-[#050A13] border border-[#1b2f4a] text-[#8FA0B8]">
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="pt-3 border-t border-[#1b2f4a]/50 flex items-center justify-between">
                  <span className="text-[11px] text-[#8FA0B8]">
                    {project.clientType || 'Enterprise Architecture'}
                  </span>
                  <button
                    onClick={() => onSelectProject(project)}
                    className="text-xs font-bold text-[#159DE8] group-hover:text-[#FF851B] flex items-center gap-1 transition-colors"
                  >
                    <span>Read Details</span>
                    <ArrowRight size={13} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

      </section>

      {/* Consultation Banner */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="p-8 sm:p-12 rounded-3xl bg-gradient-to-r from-[#0B2342] to-[#071426] border border-[#1b2f4a] flex flex-col sm:flex-row items-center justify-between gap-6 text-center sm:text-left">
          <div className="space-y-2">
            <h2 className="text-2xl font-bold text-white font-display">Have a bespoke product concept in mind?</h2>
            <p className="text-sm text-[#D5DFEC]">Let's collaborate to architect an industry-defining solution.</p>
          </div>
          <button
            onClick={onOpenConsultation}
            className="px-8 py-3.5 rounded-full bg-gradient-to-r from-[#FF5A36] to-[#FF851B] text-white text-sm font-bold shadow-lg hover:shadow-glow-orange transition-all shrink-0 flex items-center gap-2"
          >
            <span>Discuss Your Project</span>
            <ArrowRight size={16} />
          </button>
        </div>
      </section>

    </div>
  );
};
